import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store";

interface MenuItem {
    name: string;
    path: string;
    icon: string;
    roles: string[];
    group?: string;
}

interface MenuGroup {
    name: string;
    items: MenuItem[];
}

interface SidebarProps {
    isMobileMenuOpen?: boolean;
    onMobileMenuToggle?: () => void;
    isDesktopSidebarCollapsed?: boolean;
    onDesktopSidebarToggle?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
    isMobileMenuOpen = false,
    onMobileMenuToggle,
    isDesktopSidebarCollapsed = false,
    onDesktopSidebarToggle,
}) => {
    const { user } = useSelector((state: RootState) => state.auth);
    const pendingRequests = useSelector(
        (state: RootState) => state.pendingRequests
    );
    const { t } = useTranslation("common");
    const location = useLocation();
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
        new Set([
            "users",
            "finance",
            "content",
            "programs",
            "admin_messaging",
            "system",
            "staking",
        ])
    );

    const menuItems: MenuItem[] = [
        {
            name: t("navigation.home"),
            path: "/dashboard",
            icon: "🏠",
            roles: ["admin", "editor"],
        },
        {
            name: t("navigation.users"),
            path: "/dashboard/users",
            icon: "👥",
            roles: ["admin"],
            group: "users",
        },
        {
            name: t("navigation.bloggers"),
            path: "/dashboard/bloggers",
            icon: "📣",
            roles: ["admin"],
            group: "users",
        },
        {
            name: "Меценаты",
            path: "/dashboard/patrons",
            icon: "🤝",
            roles: ["admin"],
            group: "users",
        },
        {
            name: t("navigation.tree"),
            path: "/dashboard/tree",
            icon: "🌳",
            roles: ["admin"],
            group: "users",
        },
        {
            name: t("navigation.roles"),
            path: "/dashboard/roles",
            icon: "🔐",
            roles: ["admin"],
            group: "users",
        },
        {
            name: "Вывод средств",
            path: "/dashboard/admin/withdrawals",
            icon: "💸",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: t("navigation.crypto_wallets"),
            path: "/dashboard/crypto-wallets",
            icon: "💳",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: "Bingx заявки",
            path: "/dashboard/admin/bingx",
            icon: "🏦",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: t("navigation.admin_topup"),
            path: "/dashboard/admin-topup",
            icon: "💳",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: "Гарант-сделки",
            path: "/dashboard/admin/garants",
            icon: "🛡️",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: "Пополнения",
            path: "/dashboard/payment-income",
            icon: "💵",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: "Статистика",
            path: "/dashboard/payments",
            icon: "💰",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: "Altyn.One",
            path: "/dashboard/admin/altyn",
            icon: "🟡",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: "FriendlyPay",
            path: "/dashboard/admin/friendlypay",
            icon: "🟢",
            roles: ["admin"],
            group: "finance",
        },
        // {
        //     name: "Adam Stripe Proxy",
        //     path: "/dashboard/adam-proxy-stats",
        //     icon: "🧩",
        //     roles: ["admin"],
        //     group: "finance",
        // },
        {
            name: "Stripe",
            path: "/dashboard/stripe-stats",
            icon: "💳",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: "Tribute",
            path: "/dashboard/tribute-stats",
            icon: "💎",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: "Автоплатежи",
            path: "/dashboard/payment-stats",
            icon: "📊",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: t("navigation.russia_hands"),
            path: "/dashboard/russia-hands",
            icon: "👥",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: "Jivo регистрации",
            path: "/dashboard/admin/jivo",
            icon: "💬",
            roles: ["admin"],
            group: "finance",
        },
        {
            name: "Билеты",
            path: "/dashboard/admin/tickets",
            icon: "🎫",
            roles: ["admin"],
            group: "finance",
        },

        {
            name: "Новости",
            path: "/dashboard/news",
            icon: "📰",
            roles: ["admin", "editor"],
            group: "content",
        },
        {
            name: "Справка",
            path: "/dashboard/help",
            icon: "❓",
            roles: ["admin"],
            group: "content",
        },
        {
            name: "Наша команда",
            path: "/dashboard/team",
            icon: "👨‍👩‍👧‍👦",
            roles: ["admin"],
            group: "content",
        },

        {
            name: "Инвест-проекты",
            path: "/dashboard/projects",
            icon: "🚀",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: "Автореферальные программы",
            path: "/dashboard/auto-referral-programs",
            icon: "🎯",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: t("navigation.marathons"),
            path: "/dashboard/marathons",
            icon: "🏃",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: t("navigation.marathon_statistics"),
            path: "/dashboard/marathon-statistics",
            icon: "📊",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: "Статистика Сейфа",
            path: "/dashboard/safe-statistics",
            icon: "🔒",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: "Контроль Звёздная",
            path: "/dashboard/star-control",
            icon: "⭐",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: "Сравнение дат программ",
            path: "/dashboard/programs/payment-compare",
            icon: "🗓️",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: "Контроль Благотворительность",
            path: "/dashboard/charity-control",
            icon: "💝",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: "Зарплатная кубышка",
            path: "/dashboard/salary-piggy-bank",
            icon: "🐷",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: "Рекламная кубышка",
            path: "/dashboard/advertising-fund",
            icon: "📢",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: t("navigation.programs"),
            path: "/dashboard/programs",
            icon: "📚",
            roles: ["admin"],
            group: "programs",
        },
        {
            name: "Инвестиции",
            path: "/dashboard/investments",
            icon: "📈",
            roles: ["admin"],
            group: "staking",
        },
        {
            name: "Стейкинг",
            path: "/dashboard/staking",
            icon: "💰",
            roles: ["admin"],
            group: "staking",
        },
        {
            name: "Настройка стратегий",
            path: "/dashboard/investment-strategies",
            icon: "⚙️",
            roles: ["admin"],
            group: "staking",
        },
        {
            name: "Ваучеры",
            path: "/dashboard/vouchers",
            icon: "🎫",
            roles: ["admin"],
            group: "vouchers",
        },
        {
            name: "Кредитные",
            path: "/dashboard/credit-shares",
            icon: "🎫",
            roles: ["admin"],
            group: "vouchers",
        },
        {
            name: "Купленные",
            path: "/dashboard/purchased-shares",
            icon: "👑",
            roles: ["admin"],
            group: "vouchers",
        },
        {
            name: "Подарочные",
            path: "/dashboard/gifted-shares",
            icon: "🎁",
            roles: ["admin"],
            group: "vouchers",
        },

        {
            name: "Массовые рассылки",
            path: "/dashboard/admin/messaging",
            icon: "📢",
            roles: ["admin"],
            group: "admin_messaging",
        },
        {
            name: "Трансляции Millennium",
            path: "/dashboard/admin/millennium-broadcast",
            icon: "📡",
            roles: ["admin"],
            group: "admin_messaging",
        },
        {
            name: t("navigation.withdrawal_receipts"),
            path: "/dashboard/admin/withdrawal-receipts",
            icon: "🧾",
            roles: ["admin"],
            group: "admin_messaging",
        },
        {
            name: "Gold 1000 контроль",
            path: "/dashboard/admin/gold-control",
            icon: "🥇",
            roles: ["admin"],
            group: "admin_messaging",
        },
        {
            name: t("navigation.settings"),
            path: "/dashboard/settings",
            icon: "⚙️",
            roles: ["admin"],
            group: "system",
        },
        {
            name: "API ключи",
            path: "/dashboard/apikeys",
            icon: "🔑",
            roles: ["admin"],
            group: "system",
        },
        {
            name: t("navigation.api_statistics"),
            path: "/dashboard/api-statistics",
            icon: "📈",
            roles: ["admin"],
            group: "system",
        },
        {
            name: "Статистика запросов API",
            path: "/dashboard/api-stats",
            icon: "📊",
            roles: ["admin"],
            group: "system",
        },
        {
            name: t("navigation.webhooks"),
            path: "/dashboard/webhooks",
            icon: "🔗",
            roles: ["admin"],
            group: "system",
        },
        {
            name: t("navigation.webhook_endpoints"),
            path: "/dashboard/webhook-endpoints",
            icon: "🔌",
            roles: ["admin"],
            group: "system",
        },
        {
            name: "Телеграм логи",
            path: "/dashboard/telegram-logs",
            icon: "📱",
            roles: ["admin"],
            group: "system",
        },
        {
            name: "Поощрительный фонд",
            path: "/dashboard/incentive-fund",
            icon: "✨",
            roles: ["admin"],
            group: "millennium",
        },
        {
            name: "Бонусный фонд",
            path: "/dashboard/game-bonus",
            icon: "🎁",
            roles: ["admin"],
            group: "millennium",
        },
        {
            name: "Игровые операции",
            path: "/dashboard/token-operations",
            icon: "🎮",
            roles: ["admin"],
            group: "millennium",
        },
        {
            name: "Game API логи",
            path: "/dashboard/game-logger",
            icon: "🎮",
            roles: ["admin"],
            group: "millennium",
        },
        {
            name: "Аналитика лендинга",
            path: "/dashboard/landing-analytics",
            icon: "📊",
            roles: ["admin"],
            group: "millennium",
        },
    ];

    const menuBadges: Record<string, number> = {
        "/dashboard/admin/withdrawals": pendingRequests.withdrawals,
        "/dashboard/admin/bingx": pendingRequests.bingx,
        "/dashboard/admin-topup": pendingRequests.topups,
    };

    // Если пользователь еще не загружен, показываем базовые пункты меню
    const userRoles = user?.roles || ["user"];

    const filteredMenuItems = menuItems.filter((item) =>
        item.roles.some((role) => userRoles.includes(role))
    );

    // Группируем элементы меню
    const groupedMenuItems: MenuGroup[] = [];

    // Главная страница (без группы)
    const homeItem = filteredMenuItems.find(
        (item) => item.path === "/dashboard"
    );
    if (homeItem) {
        groupedMenuItems.push({
            name: "",
            items: [homeItem],
        });
    }

    // Группа "Пользователи"
    const usersItems = filteredMenuItems.filter(
        (item) => item.group === "users"
    );
    if (usersItems.length > 0) {
        groupedMenuItems.push({
            name: t("navigation.users_group"),
            items: usersItems,
        });
    }

    // Группа "Финансы"
    const financeItems = filteredMenuItems.filter(
        (item) => item.group === "finance"
    );
    if (financeItems.length > 0) {
        groupedMenuItems.push({
            name: t("navigation.finance_group"),
            items: financeItems,
        });
    }
    // Группа "Ваучеры"
    const vouchersItems = filteredMenuItems.filter(
        (item) => item.group === "vouchers"
    );
    if (vouchersItems.length > 0) {
        groupedMenuItems.push({
            name: "Ваучеры",
            items: vouchersItems,
        });
    }
    // Группа "Стейкинг"
    const stakingItems = filteredMenuItems.filter(
        (item) => item.group === "staking"
    );
    if (stakingItems.length > 0) {
        groupedMenuItems.push({
            name: "Стейкинг",
            items: stakingItems,
        });
    }
    // Группа "Millennium"
    const millenniumItems = filteredMenuItems.filter(
        (item) => item.group === "millennium"
    );
    if (millenniumItems.length > 0) {
        groupedMenuItems.push({
            name: "Millennium",
            items: millenniumItems,
        });
    }

    // Группа "Контент"
    const contentItems = filteredMenuItems.filter(
        (item) => item.group === "content"
    );
    if (contentItems.length > 0) {
        groupedMenuItems.push({
            name: "Контент",
            items: contentItems,
        });
    }

    // Группа "Программы"
    const programsItems = filteredMenuItems.filter(
        (item) => item.group === "programs"
    );
    if (programsItems.length > 0) {
        groupedMenuItems.push({
            name: t("navigation.programs_group"),
            items: programsItems,
        });
    }
    // Группа "Рассылки"
    const adminMessagingItems = filteredMenuItems.filter(
        (item) => item.group === "admin_messaging"
    );
    if (adminMessagingItems.length > 0) {
        groupedMenuItems.push({
            name: t("navigation.messaging_group"),
            items: adminMessagingItems,
        });
    }

    // Группа "Система"
    const systemItems = filteredMenuItems.filter(
        (item) => item.group === "system"
    );
    if (systemItems.length > 0) {
        groupedMenuItems.push({
            name: t("navigation.system_group"),
            items: systemItems,
        });
    }

    const closeMobileMenu = () => {
        if (onMobileMenuToggle) {
            onMobileMenuToggle();
        }
    };

    const toggleGroup = (groupName: string) => {
        setCollapsedGroups((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(groupName)) {
                newSet.delete(groupName);
            } else {
                newSet.add(groupName);
            }
            return newSet;
        });
    };

    const isGroupCollapsed = (groupName: string) => {
        return collapsedGroups.has(groupName);
    };

    const renderMenuItems = (items: MenuItem[]) => (
        <>
            {items.map((item) => {
                const badgeValue = menuBadges[item.path] ?? 0;
                return (
                    <Link
                        key={item.path}
                        to={item.path}
                        onClick={closeMobileMenu}
                        className={`flex items-center text-sm px-4 py-1 rounded-lg transition-colors ${
                            location.pathname === item.path
                                ? "bg-blue-600 text-white"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                        }`}
                    >
                        <span className="mr-3">{item.icon}</span>
                        <span className="flex-1">{item.name}</span>
                        {badgeValue > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-100">
                                {badgeValue}
                            </span>
                        )}
                    </Link>
                );
            })}
        </>
    );

    const renderMenuGroup = (group: MenuGroup) => {
        const groupKey = group.name.toLowerCase().replace(/\s+/g, "_");
        const isCollapsed = isGroupCollapsed(groupKey);

        return (
            <div key={group.name || "home"} className="mb-4">
                {group.name ? (
                    <>
                        <button
                            onClick={() => toggleGroup(groupKey)}
                            className="flex items-center justify-between w-full px-4 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        >
                            <span>{group.name}</span>
                            <svg
                                className={`w-4 h-4 transition-transform ${
                                    isCollapsed ? "rotate-180" : ""
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </button>
                        <div
                            className={`transition-all duration-200 ease-in-out ${
                                isCollapsed ? "max-h-0 overflow-hidden" : ""
                            }`}
                        >
                            <div className="space-y-1 pl-4">
                                {renderMenuItems(group.items)}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="space-y-1">
                        {renderMenuItems(group.items)}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {/* Мобильное меню (оверлей) */}
            {isMobileMenuOpen && (
                <div className="lg:hidden fixed inset-0 z-40">
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50"
                        onClick={closeMobileMenu}
                    ></div>
                    <div className="fixed left-0 top-0 h-full w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <img
                                src="/alny-logo.png"
                                alt="Alny Logo"
                                className="h-8 w-auto"
                            />
                            <button
                                onClick={closeMobileMenu}
                                className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        <nav className="p-4 overflow-y-auto max-h-[calc(100vh-8rem)]">
                            {groupedMenuItems.map(renderMenuGroup)}
                        </nav>
                    </div>
                </div>
            )}

            {/* Десктопный сайдбар */}
            {!isDesktopSidebarCollapsed && (
                <div className="hidden lg:block bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 w-64 min-h-screen p-4 shrink-0 transition-[width] duration-200">
                    <div className="mb-8">
                        <img
                            src="/alny-logo.png"
                            alt="Alny Logo"
                            className="h-8 w-auto mx-auto"
                        />
                    </div>

                    <nav className="overflow-y-auto max-h-[calc(100vh-8rem)]">
                        {groupedMenuItems.map(renderMenuGroup)}
                    </nav>
                </div>
            )}
        </>
    );
};

export default Sidebar;
